import { Injectable } from '@angular/core';
import {
  GET_SETTINGS,
  UPDATE_SETTINGS,
  GET_SETTINGS_DEFAULT_INTERNAL_EMAIL,
  GET_SETTINGS_COST_PERCENTAGES,
  GET_SETTINGS_TRUNCATE_COUNTS,
  SETTINGS_TRUNCATE,
} from '@queries/settings.graphql';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, Observable, switchMap, of, map } from 'rxjs';
import { DataTruncateCount, DataTruncateEntity, SettingsType, SettingsUpdateInput } from './settings.types';

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  private _settings: BehaviorSubject<SettingsType | null> = new BehaviorSubject(null);

  constructor(private readonly apollo: Apollo) {}

  get settings$(): Observable<SettingsType | null> {
    return this._settings.asObservable();
  }

  getSettings(): Observable<SettingsType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ Settings: SettingsType }>({
        query: GET_SETTINGS,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => {
          this._settings.next(data.Settings);
          return of(data.Settings);
        }),
      );
  }

  updateSettings(data: SettingsUpdateInput): Observable<SettingsType> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ SettingsUpdate: SettingsType }>({
        mutation: UPDATE_SETTINGS,
        variables: { data },
      })
      .pipe(
        switchMap(({ data: result }) => {
          this._settings.next(result.SettingsUpdate);
          return of(result.SettingsUpdate);
        }),
      );
  }

  // No admin gate on the backend side — safe to call from any authenticated
  // context, unlike getSettings() above (admin-only, also returns the
  // plaintext Resend key).
  getDefaultInternalEmail(): Observable<string | null> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ SettingsDefaultInternalEmail: string | null }>({
        query: GET_SETTINGS_DEFAULT_INTERNAL_EMAIL,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(
        switchMap(({ data }) => of(data.SettingsDefaultInternalEmail)),
      );
  }

  // No admin gate on the backend side — safe to call from the add-cost dialog
  // for any proposal user, unlike getSettings() above (admin-only, also
  // returns the plaintext Resend key).
  getCostPercentages(): Observable<{ insurancePercentage: number | null; customsPercentage: number | null }> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .watchQuery<{ SettingsCostPercentages: { insurancePercentage: number | null; customsPercentage: number | null } }>({
        query: GET_SETTINGS_COST_PERCENTAGES,
        fetchPolicy: 'network-only',
      })
      .valueChanges.pipe(map(({ data }) => data.SettingsCostPercentages));
  }

  getTruncateCounts(entity: DataTruncateEntity): Observable<DataTruncateCount[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .query<{ SettingsTruncateCounts: DataTruncateCount[] }>({
        query: GET_SETTINGS_TRUNCATE_COUNTS,
        variables: { entity },
        fetchPolicy: 'network-only',
      })
      .pipe(map(({ data }) => data.SettingsTruncateCounts));
  }

  truncate(entity: DataTruncateEntity): Observable<DataTruncateCount[]> {
    return this.apollo
      // eslint-disable-next-line @typescript-eslint/naming-convention
      .mutate<{ SettingsTruncate: DataTruncateCount[] }>({
        mutation: SETTINGS_TRUNCATE,
        variables: { entity },
      })
      .pipe(map(({ data }) => data.SettingsTruncate));
  }
}
